import { Divider, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useQuantity } from "src/hooks/useQuantity";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import Subheader from "src/pages/Onboarding/Payment/components/Headers/Subheader";
import HSAFSADisclaimer from "src/pages/Onboarding/Payment/components/HSAFSADisclaimer";
import InformationArray from "src/pages/Onboarding/Payment/components/InformationArray";
import PaymentProcessor from "src/pages/Onboarding/Payment/components/PaymentProcessor";
import QuantitySelection from "src/pages/Onboarding/Payment/components/QuantitySelection";
import { medicationInfoMap } from "src/pages/Onboarding/Payment/utils/mapMedicationInfoToProduct";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import WhiteCard from "src/shared/components/WhiteCard";
import { handleFocusIntoView } from "src/utils/focusIntoView";

const EmergencyContraceptionDisplay = () => {
  const isMobile = useIsMobile();
  const [selectedProduct] = useSelectedProduct();
  const category = useProduct((p) => p.category.name);
  const productSlug = useProduct((p) => p.slug);
  const { t } = useTranslation();
  const page = "payment-information";
  const [quantity, setQuantity] = useQuantity(1);

  const medicationInfo = useMemo(
    () => medicationInfoMap[productSlug],
    [productSlug],
  );

  const handleSelectQuantity = useCallback(
    (q: number) => {
      setQuantity(q);
    },
    [setQuantity],
  );

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    const total = selectedProduct.amount * quantity;

    return total.toLocaleString();
  }, [quantity, selectedProduct]);

  const buttonText = useMemo(
    () => t(`${page}.${productSlug}.submit`, "Submit to provider"),
    [productSlug, t],
  );

  return (
    <>
      <Stack sx={{ position: "relative" }}>
        <HSAFSADisclaimer category={category} showHSAFSA />
        <WhiteCard
          onClick={() => handleFocusIntoView("payment-input")}
          sx={{
            mt: "-15px", // pulls it up by the same radius
            borderRadius: "0 0 10px 10px",
            px: 2,
            paddingBottom: isMobile ? "20px" : "0px",
            zIndex: 1,
            cursor: "pointer",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ position: "relative", bottom: isMobile ? 0 : 8 }}
          >
            <Stack gap={1}>
              <Typography variant={isMobile ? "body1" : "h5"} fontWeight={600}>
                {t(medicationInfo.header)}
              </Typography>
              {medicationInfo.text != null ? (
                <Typography variant={isMobile ? "body2" : "h6"}>
                  {t(medicationInfo.text)}
                </Typography>
              ) : null}
            </Stack>
            <MedicationIcon
              slug={productSlug}
              name={medicationInfo.name}
              width={isMobile ? 130 : 180}
              height={isMobile ? 130 : 180}
            />
          </Stack>
        </WhiteCard>
      </Stack>
      <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
        <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
          <Subheader />
        </Typography>
        <InformationArray />
      </Stack>
      <QuantitySelection
        quantity={quantity}
        handleSelectQuantity={handleSelectQuantity}
        options={[1, 3, 6, 9]}
      />
      <Stack gap={3}>
        <Stack gap={4}>
          <Stack gap={2}>
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
              <Typography alignSelf={"end"}>
                {t(`${page}.hsaDisclaimer`)}
              </Typography>
            </Stack>
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={null}
              button={buttonText}
              dueToday={Number(defaultProductPrice)}
            />
          ) : null}
        </Stack>
      </Stack>
    </>
  );
};

export default EmergencyContraceptionDisplay;
